<template>
  <fieldset class="product-swatches">
    <legend class="product-swatches__legend caption">
      <div class="product-swatches__legend-content">
        <span class="product-swatches__label text-label-xs text-label-s-desktop">
          <span v-text="optionName" />
        </span>
      </div>
    </legend>

    <div
      v-if="productLink && swatchProducts.length > 0"
      class="product-swatches__list"
    >
      <template

        v-for="(linkedProduct, index) in swatchProducts"
        :key="index"
      >
        <div
          v-if="linkedProduct?.title"
          :class="getACtiveLinkedProduct(linkedProduct.title)"
          class="product-swatches__item"
          :data-value="formatValue(linkedProduct.title.toLowerCase())"
          @click="handleLinkedProduct(linkedProduct)"
          @keydown.enter="handleLinkedProduct(linkedProduct)"
          tabindex="0"
        >
          <label
            class="product-swatches__input-label"
            :for="formatValue(linkedProduct.title)"
          >
            <span
              class="text-body-s"
              v-text="formatLabel(formatValue(linkedProduct.title))"
            />
          </label>
        </div>
      </template>
    </div>

    <div
      v-else
      class="product-swatches__list"
    >
      <template
        v-for="(value) in option.values"
        :key="value"
      >
        <div
          class="product-swatches__item"
          :class="getSwatchItemClasses(value)"
          :data-value="formatValue(value.toLowerCase())"
        >
          <input
            :id="`${option.name}-${formatValue(value)}`"
            :ref="formatValue(value)"
            class="product-swatches__input visually-hidden"
            :name="option.name"
            type="radio"
            :value="formatValue(value)"
            :checked="value === selectedOption?.value"
            @change="handleOptionChange(value)"
            @focus="handleFocusChange(value)"
            @blur="handleBlurChange(value)"
          >

          <label
            class="product-swatches__input-label"
            :for="`${option.name}-${formatValue(value)}`"
            :class="formatValue(value)"
          >
            <span
              class="text-body-s"
              v-text="value"
            />
          </label>
        </div>
      </template>
    </div>
    <p class="product-swatches__gift-description text-body-s"
      v-if="giftCardOptionDesc"
      v-text="giftCardOptionDesc"
    />
  </fieldset>
</template>

<script>

/**
 * Vue: Product swatches (product-swatches)
 * -----------------------------------------------------------------------------
 * Product swatches to handle displaying and selecting product options.
 *
 * @param {Object} product - Formatted product object.
 * @param {Object} option - Option being rendered.
 * @param {Array} selectedOptions - Currently selected options.
 * @param {Boolean} showSwatchError - Show swatch error message for this option.
 *
 */

import { isVariantOptionAvailable } from '~/helpers/product'
import { convertToEscapedValue } from '~/helpers/convert'

export default {
  name: 'ProductSwatches',

  props: {
    productLink: {
      type: [
        Boolean,
        Object,
      ],
      default: false,
    },
    option: {
      type: [
        Boolean,
        Object,
      ],
      default: false,
    },
    product: {
      type: [
        Boolean,
        Object,
      ],
      default: false,
    },
    swatchProducts: {
      type: [
        Array,
        Object,
      ],
      default() {
        return []
      },
    },
    selectedOptions: {
      type: Array,
      default() {
        return []
      },
    },
    selectedLinkedOptions: {
      type: Object,
      default() {
        return {}
      },
    },
    forceUpdateOptions: {
      type: Boolean,
      default: false,
    },
    giftCardOptionDesc: {
      type: [Boolean, String],
      default: false,
    },
    showSwatchError: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['linked-option-change','option-change'],

  data() {
    return {
      availableOptions: [],
      swatchFocused: '',
    }
  },

  computed: {
    optionName() {
      const prefix = this.$string('product.form.option_title_prefix') || ''

      return `${prefix} ${this.option.name}`
    },

    /**
     * Compute the selected option data for the current option.
     * @returns {Object}
     */
    selectedOption() {
      if (!this.selectedOptions) {
        return
      }

      return this.selectedOptions[this.option.index]
    },
  },

  watch: {
    selectedOptions() {
      this.updateAvailableOptions()
    },

    forceUpdateOptions() {
      this.updateSelectedOption()
    },
  },

  mounted() {
    // not empty
  },


  methods: {

    /**
     * Updates the selected option to one that is available
     * if the current selection option is non existent
     */
    updateSelectedOption() {
      if (this.availableOptions < 1) {
        return
      }

      this.updateAvailableOptions()
      this.handleOptionChange(this.availableOptions[0])
      this.$refs[this.availableOptions[0]][0].checked = true
    },

    /**
     * Handle blur change
     * @param {String} value - String of selected option name.
     */
    handleBlurChange(value) {
      this.swatchFocused = this.swatchFocused.replace(value, '')
    },

    /**
     * Handle focus change
     * @param {String} value - String of selected option name.
     */
    handleFocusChange(value) {
      this.swatchFocused = value
    },

    /**
     * Handle change event on the swatch product linked.
     * @param {String} value - String of selected option name.
     */
    handleLinkedProduct(value) {
      this.$emit('linked-option-change', {
        product: value,
      })
    },

    /**
     * Handle change event on the swatch input element.
     * @param {String} value - String of selected option name.
     */
    handleOptionChange(value) {
      this.$emit('option-change', this.option.index, {
        name: this.option.name,
        value,
      })
    },

    /**
     * Gets CSS Classes for `product-swatches__item` element.
     * @returns {Object}
     */
    getSwatchItemClasses(value) {
      return {
        [this.$class('active')]: value === this.selectedOption?.value,
        [this.$class('disabled')]: this.isOptionDisabled(value),
        [this.$class('hidden')]: this.isOptionUnAvailable(value),
        [this.$class('focus')]: this.swatchFocused === value,
      }
    },

    /**
     * Gets CSS Classes for `product-swatches__item` element.
     * @returns {Object}
     */
    getACtiveLinkedProduct(value) {
      return {
        [this.$class('active')]: value === this.selectedLinkedOptions?.value,
      }
    },

    /**
     * Returns the option value disabled state.
     * - Gets variants and finds availability.
     * @param {String} optionValue - The value to check.
     * @returns {Boolean}
     */
    isOptionDisabled(optionValue) {
      if (!this.product) {
        return true
      }

      const options = [...this.selectedOptions].map((option) => {
        option.value
      })
      options[this.option.index] = optionValue

      const isAvailable = isVariantOptionAvailable(this.product, options)

      return !isAvailable
    },

    /**
     * Updates the available options relative to the first (this.option[0]) selected value
     */
    updateAvailableOptions() {
      if (this.option.index === 0) {
        return
      }
      const availableOptions = []
      const rootOption = this.selectedOptions[0]
      const findAvailableOption = this.product.variants.filter((variant) => {
        return variant.selectedOptions[0].value === rootOption.value
      })

      findAvailableOption.forEach((option) => {
        availableOptions.push(option.selectedOptions[this.option.index].value)
      })

      this.availableOptions = availableOptions
    },

    /**
     * Evaluates if the option value is available
     * - this is different from out of stock unavailable
     * - it is checking if value is non existent
     * @param {String} value - Option value
     * @returns {Boolean}
     */
    isOptionUnAvailable(value) {
      if (this.option.index === 0) {
        return false
      }

      return !this.availableOptions.includes(value)
    },

    /**
     * Removes title to format variant label
     * @param {String} label - Option value
     * @returns {String}
     */
    formatLabel(label) {
      return label.split('-').pop().trim()
    },

    /**
     * Clean special characters from opion value
     * @param {String} string - Option value
     * @returns {String}
     */
    formatValue(string) {
      return convertToEscapedValue(string)
    },
  },
}
</script>

<style lang="scss">
@import './product-swatches';
</style>
